import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StatusBadge from "@/components/common/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Camera, Save, Download, PenTool, Trash2, PlusCircle,
  CheckCircle2, ChevronDown, ChevronUp, GripVertical, Home, Gauge, KeyRound, FileText, GitCompareArrows,
} from "lucide-react";

const CONDITION_TONE = {
  neuf: "success", tres_bon: "success", bon: "success",
  moyen: "warning", use: "warning", degrade: "danger", absent: "danger",
};
const STATUS_TONE = { draft: "warning", signed: "success", archived: "neutral" };
const AUTOSAVE_MS = 30000;

export default function InventoryEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [meta, setMeta] = useState({ conditions: [], meter_kinds: [], key_kinds: [], retention_kinds: [] });
  const [tab, setTab] = useState("rooms");
  const dirty = useRef(false);

  const load = useCallback(() => api.get(`/inventories/${id}`).then(({ data }) => setInv(data)), [id]);
  useEffect(() => { load(); api.get("/inventories/meta").then(({ data }) => setMeta(data)); }, [load]);

  // Autosave every 30 seconds if dirty
  useEffect(() => {
    const t = setInterval(async () => {
      if (dirty.current && inv) {
        try { await api.patch(`/inventories/${id}`, { executor_name: inv.executor_name || undefined, executor_role: inv.executor_role || undefined, notes: inv.notes || undefined }); dirty.current = false; }
        catch { /* silent */ }
      }
    }, AUTOSAVE_MS);
    return () => clearInterval(t);
  }, [inv, id]);

  if (!inv) return <div className="p-8 text-muted-foreground">Chargement…</div>;

  const markDirty = () => { dirty.current = true; };

  const setField = (k, v) => { setInv({ ...inv, [k]: v }); markDirty(); };

  const genPDF = async () => {
    try {
      const { data } = await api.post(`/inventories/${id}/pdf`);
      toast.success("PDF généré");
      setInv({ ...inv, pdf: data });
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/inventories/${id}/pdf/download`, "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  return (
    <div className="animate-fade-up pb-24" data-testid="inventory-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <Button variant="ghost" onClick={() => navigate("/app/inventories")} data-testid="back-btn"><ArrowLeft className="mr-2 h-4 w-4" />Retour</Button>
        <div className="flex items-center gap-2">
          <StatusBadge tone={STATUS_TONE[inv.status]}>{inv.status}</StatusBadge>
          {inv.pdf?.verify_url && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/inventories/${id}/pdf/download`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="dl-pdf-btn"><Download className="mr-2 h-3 w-3" />PDF</Button>
            </a>
          )}
          <Button className="btn-pill" onClick={genPDF} data-testid="gen-pdf-btn"><FileText className="mr-2 h-4 w-4" />Générer PDF</Button>
        </div>
      </div>

      <div className="card-elevated p-5 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {inv.kind === "entry" ? "État des lieux d'entrée" : inv.kind === "exit" ? "État des lieux de sortie" : inv.kind === "intermediate" ? "État intermédiaire" : "Visite de contrôle"}
        </div>
        <div className="mt-1 font-heading text-2xl">EDL #{id.slice(0, 8).toUpperCase()}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 text-sm">
          <Field label="Intervenant">
            <Input value={inv.executor_name || ""} onChange={(e) => setField("executor_name", e.target.value)} placeholder="Nom" data-testid="executor-name" />
          </Field>
          <Field label="Rôle">
            <Input value={inv.executor_role || ""} onChange={(e) => setField("executor_role", e.target.value)} placeholder="Gestionnaire, huissier…" />
          </Field>
          <Field label="Notes">
            <Input value={inv.notes || ""} onChange={(e) => setField("notes", e.target.value)} placeholder="Observations générales" />
          </Field>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="rooms" data-testid="edl-tab-rooms"><Home className="mr-2 h-3 w-3" />Pièces</TabsTrigger>
          <TabsTrigger value="meters" data-testid="edl-tab-meters"><Gauge className="mr-2 h-3 w-3" />Compteurs</TabsTrigger>
          <TabsTrigger value="keys" data-testid="edl-tab-keys"><KeyRound className="mr-2 h-3 w-3" />Clés</TabsTrigger>
          <TabsTrigger value="deposit" data-testid="edl-tab-deposit">Dépôt</TabsTrigger>
          <TabsTrigger value="signatures" data-testid="edl-tab-signatures"><PenTool className="mr-2 h-3 w-3" />Signatures</TabsTrigger>
          <TabsTrigger value="compare" data-testid="edl-tab-compare"><GitCompareArrows className="mr-2 h-3 w-3" />Comparer</TabsTrigger>
        </TabsList>
        <TabsContent value="rooms"><RoomsTab inv={inv} meta={meta} reload={load} /></TabsContent>
        <TabsContent value="meters"><MetersTab inv={inv} meta={meta} reload={load} /></TabsContent>
        <TabsContent value="keys"><KeysTab inv={inv} meta={meta} reload={load} /></TabsContent>
        <TabsContent value="deposit"><DepositTab inv={inv} meta={meta} reload={load} /></TabsContent>
        <TabsContent value="signatures"><SignaturesTab inv={inv} reload={load} /></TabsContent>
        <TabsContent value="compare"><CompareTab inv={inv} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs">{label}</Label>{children}</div>;
}

/* ---------------- Rooms ---------------- */
function RoomsTab({ inv, meta, reload }) {
  const [openRoom, setOpenRoom] = useState(inv.rooms?.[0]?.id || null);
  const [addOpen, setAddOpen] = useState(false);
  const [newRoomName, setNewRoomName] = useState("");

  const updateItem = async (roomId, itemKey, condition, notes) => {
    try {
      await api.patch(`/inventories/${inv.id}/rooms/${roomId}/items/${itemKey}`, { condition, notes });
      reload();
    } catch (e) { toast.error("Erreur"); }
  };

  const addRoom = async () => {
    try {
      await api.post(`/inventories/${inv.id}/rooms`, { name: newRoomName });
      toast.success("Pièce ajoutée"); setAddOpen(false); setNewRoomName(""); reload();
    } catch { toast.error("Erreur"); }
  };

  const removeRoom = async (rid) => {
    if (!window.confirm("Supprimer cette pièce ?")) return;
    await api.delete(`/inventories/${inv.id}/rooms/${rid}`); reload();
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <Button variant="outline" onClick={() => setAddOpen(true)} data-testid="add-room-btn"><PlusCircle className="mr-2 h-4 w-4" />Ajouter une pièce</Button>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvelle pièce</DialogTitle></DialogHeader>
            <Input value={newRoomName} onChange={(e) => setNewRoomName(e.target.value)} placeholder="ex : Chambre 2, Bureau…" data-testid="new-room-name" />
            <DialogFooter><Button onClick={addRoom} data-testid="new-room-submit">Créer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {(inv.rooms || []).map((r) => (
        <div key={r.id} className="card-elevated overflow-x-auto" data-testid={`room-${r.id}`}>
          <div
            className="w-full flex items-center justify-between px-5 py-4 hover:bg-secondary/30 cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={() => setOpenRoom(openRoom === r.id ? null : r.id)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setOpenRoom(openRoom === r.id ? null : r.id); }}
          >
            <div className="flex items-center gap-3">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
              <div className="text-left">
                <div className="font-heading">{r.name}</div>
                <div className="text-xs text-muted-foreground">
                  {(r.items || []).filter(i => i.condition).length}/{(r.items || []).length} éléments renseignés
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeRoom(r.id); }}><Trash2 className="h-3 w-3" /></Button>
              {openRoom === r.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
          </div>
          {openRoom === r.id && (
            <div className="border-t divide-y divide-border">
              {(r.items || []).map((it) => (
                <ItemRow key={it.key} item={it} roomId={r.id} inventoryId={inv.id} meta={meta} onSave={(cond, notes) => updateItem(r.id, it.key, cond, notes)} onPhoto={reload} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ItemRow({ item, roomId, inventoryId, meta, onSave, onPhoto }) {
  const [condition, setCondition] = useState(item.condition || "");
  const [notes, setNotes] = useState(item.notes || "");
  const [saved, setSaved] = useState(item.condition ? true : false);
  const fileRef = useRef(null);

  const commit = async () => { await onSave(condition, notes); setSaved(true); };

  const uploadPhoto = async (file) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/inventories/${inventoryId}/photos`, {
          room_id: roomId, item_key: item.key, image_b64: reader.result, filename: file.name,
        });
        toast.success("Photo ajoutée");
        onPhoto();
      } catch (e) { toast.error(e.response?.data?.detail || "Erreur upload"); }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="px-5 py-3 flex flex-wrap items-center gap-3" data-testid={`item-${item.key}`}>
      <div className="flex-1 min-w-32">
        <div className="text-sm font-medium">{item.label}</div>
        {(item.photo_ids || []).length > 0 && <div className="text-[10px] text-muted-foreground">{item.photo_ids.length} photo(s)</div>}
      </div>
      <Select value={condition} onValueChange={(v) => { setCondition(v); setSaved(false); }}>
        <SelectTrigger className="w-40 h-11" data-testid={`cond-${item.key}`}><SelectValue placeholder="État…" /></SelectTrigger>
        <SelectContent>{meta.conditions.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
      </Select>
      <Input value={notes} onChange={(e) => { setNotes(e.target.value); setSaved(false); }} placeholder="Notes" className="flex-1 min-w-40" />
      <Button size="sm" onClick={commit} className="h-11 px-4" data-testid={`save-${item.key}`}>
        {saved ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Save className="h-4 w-4" />}
      </Button>
      <input type="file" accept="image/*" capture="environment" ref={fileRef} className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
      <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="h-11 px-4" data-testid={`photo-${item.key}`}><Camera className="h-4 w-4" /></Button>
    </div>
  );
}

/* ---------------- Meters ---------------- */
function MetersTab({ inv, meta, reload }) {
  const [form, setForm] = useState({ kind: "eau_froide", label: "Eau froide", value: "", unit: "m³", meter_number: "" });
  const add = async () => {
    try { await api.post(`/inventories/${inv.id}/meters`, form); toast.success("Compteur ajouté"); reload(); setForm({ ...form, value: "", meter_number: "" }); }
    catch { toast.error("Erreur"); }
  };
  const remove = async (mid) => { await api.delete(`/inventories/${inv.id}/meters/${mid}`); reload(); };
  return (
    <div className="space-y-4">
      <div className="card-elevated p-5 grid grid-cols-1 md:grid-cols-6 gap-3">
        <Select value={form.kind} onValueChange={(v) => { const k = meta.meter_kinds.find(m => m.code === v); setForm({ ...form, kind: v, label: k?.label || v, unit: k?.unit || form.unit }); }}>
          <SelectTrigger className="md:col-span-2" data-testid="meter-kind"><SelectValue /></SelectTrigger>
          <SelectContent>{meta.meter_kinds.map((m) => <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input placeholder="Relevé" type="number" step="0.001" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="meter-value" />
        <Input placeholder="Unité" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
        <Input placeholder="N° compteur" value={form.meter_number} onChange={(e) => setForm({ ...form, meter_number: e.target.value })} />
        <Button onClick={add} disabled={!form.value} data-testid="meter-add">Ajouter</Button>
      </div>
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr className="text-left"><th className="px-6 py-3">Compteur</th><th className="px-6 py-3">N°</th><th className="px-6 py-3 text-right">Relevé</th><th className="px-6 py-3">Lecture</th><th></th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(inv.meters || []).length === 0 && <tr><td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">Aucun relevé.</td></tr>}
            {(inv.meters || []).map((m) => (
              <tr key={m.id} data-testid={`meter-row-${m.id}`}>
                <td className="px-6 py-3 font-medium">{m.label}</td>
                <td className="px-6 py-3 text-xs text-muted-foreground">{m.meter_number || "—"}</td>
                <td className="px-6 py-3 text-right font-mono">{m.value} {m.unit}</td>
                <td className="px-6 py-3 text-xs">{m.read_at?.slice(0, 10)}</td>
                <td className="px-6 py-3 text-right"><Button variant="ghost" size="sm" onClick={() => remove(m.id)}><Trash2 className="h-3 w-3" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Keys ---------------- */
function KeysTab({ inv, meta, reload }) {
  const [keys, setKeys] = useState(inv.keys || []);
  const addRow = () => setKeys([...keys, { kind: "cles", count: 1, notes: "" }]);
  const save = async () => { try { await api.put(`/inventories/${inv.id}/keys`, keys.filter(k => k.count > 0)); toast.success("Clés enregistrées"); reload(); } catch { toast.error("Erreur"); } };
  return (
    <div className="space-y-4">
      <div className="card-elevated p-5 space-y-3">
        {keys.map((k, idx) => (
          <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <Select value={k.kind} onValueChange={(v) => { const nk = [...keys]; nk[idx] = { ...k, kind: v }; setKeys(nk); }}>
              <SelectTrigger className="md:col-span-2"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.key_kinds.map((kk) => <SelectItem key={kk.code} value={kk.code}>{kk.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" min="0" value={k.count} onChange={(e) => { const nk = [...keys]; nk[idx] = { ...k, count: parseInt(e.target.value) || 0 }; setKeys(nk); }} data-testid={`key-count-${idx}`} />
            <Input placeholder="Notes" value={k.notes || ""} onChange={(e) => { const nk = [...keys]; nk[idx] = { ...k, notes: e.target.value }; setKeys(nk); }} className="md:col-span-2" />
          </div>
        ))}
        <div className="flex justify-between">
          <Button variant="outline" onClick={addRow} data-testid="key-add-row"><PlusCircle className="mr-2 h-4 w-4" />Ajouter une ligne</Button>
          <Button onClick={save} className="btn-pill" data-testid="key-save">Enregistrer</Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Deposit ---------------- */
function DepositTab({ inv, meta, reload }) {
  const [rets, setRets] = useState(inv.deposit_estimation?.retentions || []);
  const [notes, setNotes] = useState(inv.deposit_estimation?.notes || "");
  const addRet = () => setRets([...rets, { kind: "menage", label: "", amount: 0 }]);
  const total = rets.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const save = async () => {
    try {
      const payload = { retentions: rets.filter(r => r.label && r.amount).map(r => ({ ...r, amount: r.amount })), notes };
      await api.put(`/inventories/${inv.id}/deposit-estimation`, payload);
      toast.success("Estimation enregistrée"); reload();
    } catch { toast.error("Erreur"); }
  };
  return (
    <div className="card-elevated p-5 space-y-4">
      <div className="text-sm text-muted-foreground">Les montants ci-dessous sont une estimation. Le calcul définitif du dépôt de garantie reste à valider par le bailleur.</div>
      {rets.map((r, idx) => (
        <div key={idx} className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <Select value={r.kind} onValueChange={(v) => { const nr = [...rets]; nr[idx] = { ...r, kind: v }; setRets(nr); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{meta.retention_kinds.map((k) => <SelectItem key={k.code} value={k.code}>{k.label}</SelectItem>)}</SelectContent>
          </Select>
          <Input className="md:col-span-2" placeholder="Libellé (ex : peinture salon)" value={r.label} onChange={(e) => { const nr = [...rets]; nr[idx] = { ...r, label: e.target.value }; setRets(nr); }} data-testid={`ret-label-${idx}`} />
          <Input type="number" placeholder="Montant €" value={r.amount} onChange={(e) => { const nr = [...rets]; nr[idx] = { ...r, amount: parseFloat(e.target.value) || 0 }; setRets(nr); }} data-testid={`ret-amount-${idx}`} />
          <Input className="md:col-span-2" placeholder="Devis / facture / notes" value={r.notes || ""} onChange={(e) => { const nr = [...rets]; nr[idx] = { ...r, notes: e.target.value }; setRets(nr); }} />
        </div>
      ))}
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes générales" />
      <div className="flex items-center justify-between border-t pt-4">
        <div><Button variant="outline" onClick={addRet} data-testid="ret-add"><PlusCircle className="mr-2 h-4 w-4" />Retenue</Button></div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total estimé (non définitif)</div>
          <div className="font-serif text-2xl">{total.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</div>
        </div>
        <Button onClick={save} className="btn-pill" data-testid="ret-save">Enregistrer</Button>
      </div>
    </div>
  );
}

/* ---------------- Signatures ---------------- */
function SignaturesTab({ inv, reload }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <SignatureBox inv={inv} party="landlord" title="Signature du bailleur" existing={inv.signatures?.landlord} reload={reload} />
      <SignatureBox inv={inv} party="tenant" title="Signature du locataire" existing={inv.signatures?.tenant?.[0]} reload={reload} />
    </div>
  );
}

function SignatureBox({ inv, party, title, existing, reload }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [name, setName] = useState("");
  const [saved, setSaved] = useState(!!existing);

  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const rect = c.getBoundingClientRect();
    c.width = rect.width; c.height = rect.height;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#112F24"; ctx.lineWidth = 2; ctx.lineCap = "round";
  }, [saved]);

  const start = (e) => {
    drawing.current = true;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y); ctx.stroke();
  };
  const stop = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#112F24"; ctx.lineWidth = 2;
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Nom du signataire requis"); return; }
    const dataUrl = canvasRef.current.toDataURL("image/png");
    try {
      await api.post(`/inventories/${inv.id}/signatures`, {
        party, signer_name: name, data_url: dataUrl,
      });
      toast.success("Signature enregistrée"); setSaved(true); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="card-elevated p-5">
      <div className="font-heading mb-3">{title}</div>
      {saved && existing ? (
        <div>
          <img src={existing.data_url} alt="Signature" className="w-full h-40 object-contain bg-white border rounded-lg" />
          <div className="mt-3 text-sm">
            <div><strong>{existing.signer_name}</strong></div>
            <div className="text-xs text-muted-foreground">{existing.signed_at && new Date(existing.signed_at).toLocaleString("fr-FR")}</div>
          </div>
          <Button variant="ghost" size="sm" className="mt-2" onClick={() => setSaved(false)}>Re-signer</Button>
        </div>
      ) : (
        <>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom et prénom du signataire" className="mb-3" data-testid={`sig-name-${party}`} />
          <canvas ref={canvasRef} data-testid={`sig-canvas-${party}`}
            className="w-full h-40 bg-white border-2 border-dashed rounded-lg touch-none cursor-crosshair"
            onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop}
            onTouchStart={start} onTouchMove={move} onTouchEnd={stop} />
          <div className="flex justify-between mt-3">
            <Button variant="ghost" size="sm" onClick={clear}>Effacer</Button>
            <Button onClick={save} className="btn-pill" data-testid={`sig-save-${party}`}><PenTool className="mr-2 h-4 w-4" />Signer</Button>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Compare ---------------- */
function CompareTab({ inv }) {
  const [others, setOthers] = useState([]);
  const [otherId, setOtherId] = useState("");
  const [result, setResult] = useState(null);
  useEffect(() => {
    api.get(`/inventories?lot_id=${inv.lot_id}`).then(({ data }) => {
      setOthers((data.items || []).filter((i) => i.id !== inv.id));
    });
  }, [inv.id, inv.lot_id]);
  const run = async () => {
    try {
      const { data } = await api.get(`/inventories/${inv.id}/compare/${otherId}`);
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const verdictTone = { improved: "success", identical: "neutral", degraded: "danger", missing: "warning" };
  const verdictLabel = { improved: "Amélioré", identical: "Identique", degraded: "Dégradé", missing: "Absent" };
  return (
    <div className="space-y-4">
      <div className="card-elevated p-5 flex items-center gap-3 flex-wrap">
        <Label>Comparer avec :</Label>
        <Select value={otherId} onValueChange={setOtherId}>
          <SelectTrigger className="w-72" data-testid="compare-other"><SelectValue placeholder="Choisir un EDL…" /></SelectTrigger>
          <SelectContent>
            {others.length === 0 && <SelectItem value="none" disabled>Aucun autre EDL pour ce lot</SelectItem>}
            {others.map((o) => <SelectItem key={o.id} value={o.id}>{o.kind} — #{o.id.slice(0, 6)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={run} disabled={!otherId || otherId === "none"} data-testid="compare-run">Comparer</Button>
      </div>
      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Améliorés" value={result.summary.improved} tone="success" />
            <MiniStat label="Identiques" value={result.summary.identical} />
            <MiniStat label="Dégradés" value={result.summary.degraded} tone="danger" />
            <MiniStat label="Absents" value={result.summary.missing} tone="warning" />
          </div>
          <div className="space-y-4">
            {result.rooms.map((r) => (
              <div key={r.room} className="card-elevated overflow-x-auto">
                <div className="px-6 py-3 bg-secondary/40 font-heading">{r.room}</div>
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground bg-secondary/20">
                    <tr className="text-left"><th className="px-4 py-2">Élément</th><th className="px-4 py-2">Entrée</th><th className="px-4 py-2">Sortie</th><th className="px-4 py-2">Verdict</th></tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {r.items.map((it) => (
                      <tr key={it.item_key}>
                        <td className="px-4 py-2 font-medium">{it.label}</td>
                        <td className="px-4 py-2 text-xs">{it.entry_label || "—"}</td>
                        <td className="px-4 py-2 text-xs">{it.exit_label || "—"}</td>
                        <td className="px-4 py-2"><StatusBadge tone={verdictTone[it.verdict]}>{verdictLabel[it.verdict]}</StatusBadge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const bg = tone === "success" ? "bg-emerald-50" : tone === "danger" ? "bg-red-50" : tone === "warning" ? "bg-amber-50" : "bg-secondary/40";
  return <div className={`${bg} rounded-xl p-4`}><div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div><div className="mt-1 font-serif text-2xl">{value}</div></div>;
}
